import { type ReactNode, useCallback, useState } from "react";
import { NEXT_PAGE, PREVIOUS_PAGE, emptyCellText, pageLabel } from "../lib/table-copy.ts";
import { atFirstPage, atLastPage, nextPage, pageOf, previousPage } from "../lib/table-page.ts";
import { Spinner } from "./Spinner.tsx";

export interface TableColumn<T> {
	id: string;
	header: string;
	accessor: (row: T) => ReactNode;
	sortable?: boolean;
	className?: string;
	/**
	 * The cell's text wraps rather than holding one line. For a column of words — a name,
	 * a note — in a table that must fit its panel: a cell that never wraps is the width
	 * the whole table then takes (3d, 30.10 — Overview).
	 */
	wrap?: boolean;
}

export type SortDirection = "asc" | "desc";

export interface TableSort {
	columnId: string;
	direction: SortDirection;
}

export interface TableProps<T> {
	columns: TableColumn<T>[];
	data: T[];
	keyExtractor: (row: T) => string;
	loading?: boolean;
	/**
	 * 🔴 The read behind `data` failed. An empty `data` then means nothing, and the
	 * empty row says `NOT_READ` instead of `emptyMessage` — which would otherwise
	 * claim an empty list over a failure (spa-review 2026-10-20, #7). The choice is
	 * `emptyCellText` in lib/table-copy.ts, not here. Screens pass this from their
	 * load state; a screen with its own failure sentence may put it in
	 * `emptyMessage` instead (the targeting section does).
	 */
	failed?: boolean;
	emptyMessage?: string;
	sort?: TableSort;
	onSortChange?: (sort: TableSort) => void;
	/**
	 * Rows per page, for a table that lives in a panel of the one-screen layout. The
	 * rows paged are the ones in `data` — what the screen read — and the choice of page
	 * is `pageOf` in lib/table-page.ts, not here. Left out, every row is rendered, as
	 * before.
	 */
	pageSize?: number;
	/** Tighter cells, for a table inside a panel. */
	dense?: boolean;
	className?: string;
}

function SortIcon({ direction }: { direction?: SortDirection }) {
	if (!direction) {
		return (
			<svg
				aria-hidden="true"
				className="ml-1 inline h-4 w-4 text-muted"
				fill="none"
				viewBox="0 0 24 24"
				strokeWidth="1.5"
				stroke="currentColor"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
				/>
			</svg>
		);
	}
	return (
		<svg
			aria-hidden="true"
			className="ml-1 inline h-4 w-4 text-content"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth="1.5"
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d={direction === "asc" ? "M4.5 15.75l7.5-7.5 7.5 7.5" : "M19.5 8.25l-7.5 7.5-7.5-7.5"}
			/>
		</svg>
	);
}

/** The sortable header's control. Non-sortable columns stay plain markup so a
 *  screen reader is not told about a button that does nothing. */
function Cell({
	sortable,
	onSort,
	label,
	children,
}: {
	sortable?: boolean;
	onSort: () => void;
	label: string;
	children: ReactNode;
}) {
	if (!sortable) return <span className="inline-flex items-center">{children}</span>;
	return (
		<button
			type="button"
			onClick={onSort}
			title={`Sort by ${label}`}
			className="inline-flex select-none items-center uppercase tracking-wider hover:text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
		>
			{children}
		</button>
	);
}

export function Table<T>({
	columns,
	data,
	keyExtractor,
	loading = false,
	failed = false,
	emptyMessage = "No data available",
	sort,
	onSortChange,
	pageSize,
	dense = false,
	className = "",
}: TableProps<T>) {
	const [internalSort, setInternalSort] = useState<TableSort | undefined>(sort);
	const [page, setPage] = useState(0);
	const shown = pageOf(data, pageSize, page);
	const cell = dense ? "px-3 py-2" : "px-6 py-4";
	const head = dense ? "px-3 py-2" : "px-6 py-3";
	const currentSort = sort ?? internalSort;

	const handleSort = useCallback(
		(columnId: string) => {
			const newDirection: SortDirection =
				currentSort?.columnId === columnId && currentSort.direction === "asc" ? "desc" : "asc";
			const newSort: TableSort = { columnId, direction: newDirection };
			if (onSortChange) {
				onSortChange(newSort);
			} else {
				setInternalSort(newSort);
			}
		},
		[currentSort, onSortChange],
	);

	return (
		<div className={className}>
			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-line">
					<thead className="bg-surface">
						<tr>
							{columns.map((column) => (
								<th
									key={column.id}
									scope="col"
									className={`${head} text-left text-xs font-medium uppercase tracking-wider text-muted ${column.className ?? ""}`}
									aria-sort={
										currentSort?.columnId === column.id
											? currentSort.direction === "asc"
												? "ascending"
												: "descending"
											: undefined
									}
								>
									{/* The sort control is a button rather than a click handler on the
									    header cell: the original was reachable by mouse only. */}
									<Cell
										sortable={column.sortable}
										onSort={() => handleSort(column.id)}
										label={typeof column.header === "string" ? column.header : column.id}
									>
										{column.header}
										{column.sortable && (
											<SortIcon
												direction={
													currentSort?.columnId === column.id ? currentSort.direction : undefined
												}
											/>
										)}
									</Cell>
								</th>
							))}
						</tr>
					</thead>
					<tbody className="divide-y divide-line bg-surface">
						{loading ? (
							<tr>
								<td colSpan={columns.length} className="px-6 py-12 text-center">
									<Spinner size="lg" className="text-muted" />
								</td>
							</tr>
						) : data.length === 0 ? (
							<tr>
								<td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-muted">
									{emptyCellText(failed, emptyMessage)}
								</td>
							</tr>
						) : (
							shown.rows.map((row) => (
								<tr key={keyExtractor(row)} className="transition-colors hover:bg-surface-2">
									{columns.map((column) => (
										<td
											key={column.id}
											className={`${column.wrap ? "whitespace-normal" : "whitespace-nowrap"} ${cell} text-sm text-content ${column.className ?? ""}`}
										>
											{column.accessor(row)}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
			{shown.pages > 1 && (
				<div className="flex items-center justify-end gap-2 border-t border-line px-3 py-2 text-xs text-muted">
					<span>{pageLabel(shown.from, shown.to, shown.read)}</span>
					<button
						type="button"
						disabled={atFirstPage(shown)}
						onClick={() => setPage(previousPage(shown))}
						className="rounded px-2 py-1 hover:bg-surface-2 hover:text-content disabled:opacity-40"
					>
						{PREVIOUS_PAGE}
					</button>
					<button
						type="button"
						disabled={atLastPage(shown)}
						onClick={() => setPage(nextPage(shown))}
						className="rounded px-2 py-1 hover:bg-surface-2 hover:text-content disabled:opacity-40"
					>
						{NEXT_PAGE}
					</button>
				</div>
			)}
		</div>
	);
}
